// Tank movement: terrain-aware acceleration, deceleration, turn rate, and
// terrain-cap collision. Constants from WinBolo bolo_map.h / tank.h.

import {
  GUNSIGHT_MAX,
  GUNSIGHT_MIN,
  GUNSIGHT_STEP_TICKS,
  MAP_SIZE,
  TANK_ACCEL_RATE,
  TANK_DECEL_RATE,
  TANK_MAX_CARRIED_PILLS,
  TANK_TURN_RAMPUP_TICKS,
  TANK_WALL_SLOW_DOWN,
  TANK_WATER_TICKS,
  Terrain,
  TERRAIN_MASK,
  TERRAIN_MINE,
} from '@bolo2k/shared';
import { detonateMine, drownTank } from './damage.js';
import { dirVector, snapDir, tileX, tileY } from './math.js';
import { TankKey, type TankInput, hasKey } from './input.js';
import type { Boat, GameState, Tank } from './types.js';

// Terrain-indexed speed cap (world-units/tick). Indices match
// @bolo2k/shared Terrain enum. Values from [Cheshire] Maps&BrainsFAQ
// ("What are the various maximum speeds of things in Bolo?") — the canonical
// per-terrain Tank-speed column. WinBolo bolo_map.h:74-86 agrees on every
// row EXCEPT BOAT (WinBolo has 16 with a "Was 3 - check??" comment); we
// follow Cheshire here. Tank-riding-a-boat speed (16) is governed by the
// boat entity's BOAT_MAX_SPEED, not this table — a tank in DeepSea/River
// with a boat doesn't read the terrain cap, the boat does.
const TERRAIN_TOP_SPEED: readonly number[] = [
  0, // Building
  3, // River
  3, // Swamp
  3, // Crater
  16, // Road
  6, // Forest
  3, // Rubble
  12, // Grass
  0, // HalfBuilding
  3, // Boat (parked-boat tile, NOT tank-riding-boat — see BOAT_MAX_SPEED)
  0, // DeepSea (lethal — handled separately)
  16, // RefBase
  0, // Pillbox (impassable)
  0, // Unknown
];

// Turn rate in 1/256-circle units per tick. Values mirror WinBolo
// bolo_map.h:107-118 exactly — Cheshire's Maps&BrainsFAQ publishes per-
// terrain speeds but not turn rates, so WinBolo is the canonical source
// here. The 0.25 values for rough terrain (river/swamp/crater/rubble) are
// what give Bolo its "manoeuvre trap" — getting caught in rough terrain
// commits you to your current heading for several seconds, distinct from
// the speed penalty. TANK_TURN_RAMPUP_TICKS still halves the first 10
// ticks (~0.2 s) of any turn so tap-response stays usable everywhere.
const TERRAIN_TURN_RATE: readonly number[] = [
  0, // Building (impassable — tank can't be on it)
  0.25, // River
  0.25, // Swamp
  0.25, // Crater
  1.0, // Road
  0.5, // Forest
  0.25, // Rubble
  1.0, // Grass
  0, // HalfBuilding (impassable)
  1.0, // Boat (tank-on-boat — same as road; boats are agile by design)
  0.5, // DeepSea (drowning kills tank anyway)
  1.0, // RefBase
  0, // Pillbox (impassable)
  0.5, // Unknown (defensive fallback)
];

// Pure tank-movement: updates only the tank's own fields (x/y/dir/speed/
// turnHoldTicks) based on input + the terrain it stands on. No terrain
// mutations, no entity damage. Returns the previous tile the tank was on
// (so callers can detect tile transitions) and the new tile entered (or
// `null` if the tank didn't change tiles this step).
//
// Used both server-side (tickTankMovement wraps this and adds mine
// detonation) and client-side (the predictor calls this directly to mirror
// server motion under the player's input).
export function predictTankMovement(
  terrain: Uint8Array,
  tank: Tank,
  input: TankInput,
): { prevTileX: number; prevTileY: number; nextTileX: number; nextTileY: number; entered: boolean } {
  const prevTileX = tileX(tank.x);
  const prevTileY = tileY(tank.y);
  const terrainHere = terrainAtRaw(terrain, prevTileX, prevTileY);

  // --- Slide (shell knockback) ---
  // Move slideDist world-units along slideDir per tick until slideTicks
  // expires. Mirrors WinBolo `tank.c:1554-1584`: X and Y components tested
  // INDEPENDENTLY, so a knockback that pushes a tank against a wall
  // corner still slides along whichever axis is clear instead of
  // stopping the entire slide. Test point uses the same leading-edge
  // offset as forward movement (see leadingEdge below).
  if (tank.slideTicks > 0) {
    const v = dirVector(tank.slideDir);
    const sStepX = v.dx * tank.slideDist;
    const sStepY = v.dy * tank.slideDist;
    const sx = tank.x + sStepX;
    const sy = tank.y + sStepY;
    const stx = tileX(leadingEdge(sx, sStepX));
    const sty = tileY(leadingEdge(sy, sStepY));
    const syBlocked = isImpassable(terrainAtRaw(terrain, prevTileX, sty));
    const sxBlocked = isImpassable(terrainAtRaw(terrain, stx, prevTileY));
    // Same diagonal-corner guard as forward movement — a knockback that
    // crosses ONLY the wall's diagonal-corner tile must not deposit the
    // tank centred on it.
    const sDiagBlocked =
      !sxBlocked &&
      !syBlocked &&
      (stx !== prevTileX || sty !== prevTileY) &&
      isImpassable(terrainAtRaw(terrain, stx, sty));
    if (!(syBlocked || sDiagBlocked)) {
      tank.y = clampWorld(sy);
    }
    if (!(sxBlocked || sDiagBlocked)) {
      tank.x = clampWorld(sx);
    }
    tank.slideTicks--;
  }

  // --- Turning ---
  const turnLeft = hasKey(input, TankKey.TurnLeft);
  const turnRight = hasKey(input, TankKey.TurnRight);
  if (turnLeft || turnRight) {
    let rate = TERRAIN_TURN_RATE[terrainHere] ?? 0.25;
    if (tank.turnHoldTicks < TANK_TURN_RAMPUP_TICKS) rate *= 0.5;
    tank.turnHoldTicks++;
    const delta = turnLeft ? -rate : rate;
    // dir is a fractional number in [0, 256). Don't use `& 0xff` — that
    // truncates to int and silently drops sub-integer accumulation, which
    // makes positive fractional turns (e.g. +0.5/tick on swamp) never advance.
    let nd = tank.dir + delta;
    if (nd < 0) nd += 256;
    else if (nd >= 256) nd -= 256;
    tank.dir = nd;
  } else {
    tank.turnHoldTicks = 0;
  }

  // --- Speed ---
  // Mirrors WinBolo `tankAccel` (tank.c:1702-1731): the slowdown branch wins
  // over the accelerate branch when overspeed for the current terrain — i.e.
  // tapping Faster while still cruising too fast for a slower tile decelerates
  // at TANK_DECEL_RATE/tick instead of snapping straight to the cap. Pressing
  // Slower while overspeed compounds (terrain-decel + slowkey-decel = 0.5/tick).
  const cap = TERRAIN_TOP_SPEED[terrainHere] ?? 0;
  if (hasKey(input, TankKey.Faster) && tank.speed < cap) {
    tank.speed = Math.min(cap, tank.speed + TANK_ACCEL_RATE);
  } else if (hasKey(input, TankKey.Slower)) {
    tank.speed = Math.max(0, tank.speed - TANK_DECEL_RATE);
  }
  if (tank.speed > cap) tank.speed = Math.max(0, tank.speed - TANK_DECEL_RATE);

  // --- Position ---
  // Axis-separated movement, mirroring WinBolo `tankMoveOnLand`
  // (tank.c:1488-1516). The X and Y components of the motion vector are
  // tested INDEPENDENTLY against the next tile in each axis, so a tank
  // brushing past a wall slides along it instead of stopping. Only when
  // BOTH axes are blocked (tank is driving straight into a corner) does
  // speed take a TANK_WALL_SLOW_DOWN hit. No 180° rebound — that machinery
  // is now reserved for shell knockback.
  let nextTileX = prevTileX;
  let nextTileY = prevTileY;
  let entered = false;
  // Reset the obstructed flag at the top of each tick; reasserted below
  // if any axis is blocked. Brain reads this to trigger A* recovery and
  // fire-through-walls when stuck.
  tank.obstructed = false;
  if (tank.speed > 0) {
    // Tank movement snaps to the same 16-step grid the sprite uses, so the
    // motion vector matches what the player visibly sees facing forward.
    // (Shells, pillbox aim, and boats don't snap — they use continuous dir.)
    const { dx, dy } = dirVector(snapDir(tank.dir));
    const stepX = dx * tank.speed;
    const stepY = dy * tank.speed;
    const candidateX = tank.x + stepX;
    const candidateY = tank.y + stepY;
    // Test the tile under the tank's LEADING EDGE (offset by ±~half-tile
    // in the motion direction), not under the tank's center. Without this
    // the tank — modelled as a point at its center — could push half a tile
    // into a wall before the center crossed the boundary and motion stopped.
    // See leadingEdge() docs.
    const candidateTileX = tileX(leadingEdge(candidateX, stepX));
    const candidateTileY = tileY(leadingEdge(candidateY, stepY));

    // Y axis: keep current X tile, test the proposed Y tile.
    const yBlocked = isImpassable(terrainAtRaw(terrain, prevTileX, candidateTileY));
    // X axis: keep current Y tile, test the proposed X tile.
    const xBlocked = isImpassable(terrainAtRaw(terrain, candidateTileX, prevTileY));
    // Diagonal-corner guard: WinBolo's axis-separated tests check the L-shape
    // (curX, candY) and (candX, curY) but never the diagonal (candX, candY).
    // If a wall sits ONLY at the diagonal corner, both axis tests pass and
    // the tank's centre crosses onto the wall tile — where TERRAIN_TURN_RATE
    // is 0 and the player can't rotate out ("drove diagonally into a wall
    // and got stuck"). WinBolo cuts through (its latent bug); Cheshire's
    // original evidently wedged (his Standard Autopilot avoids diagonals in
    // mazes). We complete the spec's own axis-decomposed model instead:
    // block only the MINOR axis (smaller |step| component) and let the
    // major axis slide along the wall face — the tank funnels through
    // 1-tile gaps instead of pinning at the corner. The surviving axis's
    // L-arm test already passed, so the centre still never reaches an
    // impassable tile. Exact-45° tie blocks X (deterministic; the axes are
    // symmetric there). See DECISIONS.md "Diagonal-corner slide".
    const diagBlocked =
      !xBlocked &&
      !yBlocked &&
      (candidateTileX !== prevTileX || candidateTileY !== prevTileY) &&
      isImpassable(terrainAtRaw(terrain, candidateTileX, candidateTileY));
    const diagBlocksX = diagBlocked && Math.abs(stepX) <= Math.abs(stepY);
    const diagBlocksY = diagBlocked && Math.abs(stepY) < Math.abs(stepX);
    const yEffBlocked = yBlocked || diagBlocksY;
    const xEffBlocked = xBlocked || diagBlocksX;
    if (!yEffBlocked) {
      tank.y = clampWorld(candidateY);
    }
    if (!xEffBlocked) {
      tank.x = clampWorld(candidateX);
    }
    // Both axes blocked → tank is grinding into a wall head-on. Per WinBolo
    // tank.c:1505-1509, drop speed by TANK_WALL_SLOW_DOWN (floor 0).
    if (xEffBlocked && yEffBlocked) {
      tank.speed = Math.max(0, tank.speed - TANK_WALL_SLOW_DOWN);
    }
    // Set the obstructed flag if any axis was blocked. Mirrors WinBolo
    // tank.c:1493/1505/1511/1514 — every failed-axis branch sets
    // tank->obstructed = TRUE.
    if (xEffBlocked || yEffBlocked) tank.obstructed = true;

    nextTileX = tileX(tank.x);
    nextTileY = tileY(tank.y);
    entered = nextTileX !== prevTileX || nextTileY !== prevTileY;
  }

  return { prevTileX, prevTileY, nextTileX, nextTileY, entered };
}

function isImpassable(terr: number): boolean {
  return terr === Terrain.Building || terr === Terrain.HalfBuilding || terr === Terrain.Pillbox;
}

// Compute the world-coord test point used for collision detection on one
// axis. Mirrors WinBolo `tank.c:1463-1481`: the test point is offset from
// the tank's centre by `TANK_MOVE_LAND_SUB ± 32 = 128 ± 32` world units in
// the direction of motion, placing it just inside the tank's leading edge
// (the tank is treated as a one-tile footprint, not a point). The +32 / -32
// asymmetry — pulling the test point 32 world units short of the actual
// edge — is what lets the tank poke ~32 wu (~1/8 tile, ~2 px on a 16 px
// sprite) into a wall before motion blocks. Without this offset the tank
// could overlap a wall by up to half a tile.
function leadingEdge(coord: number, step: number): number {
  if (step > 0) return coord + 160;
  if (step < 0) return coord - 96;
  return coord;
}

export function tickTankMovement(state: GameState, tank: Tank, input: TankInput): void {
  if (!tank.alive) return;
  // Gunsight range adjustment runs whether or not the tank is afloat (you
  // can dial range while ferrying ashore so the first volley off the boat
  // already aims where you want).
  tickGunsight(tank, input);
  // While in a boat, the boat drives — ignore inputs and let boat.ts handle
  // position updates. Predicted client-side movement is also suppressed; the
  // predictor has the same `inBoatId` field to read.
  if (tank.inBoatId !== 0) return;
  const m = predictTankMovement(state.terrain, tank, input);
  // Mine detonation: only when the tank actually entered a new tile (you
  // don't want to detonate the mine you're already standing on).
  if (m.entered) {
    const cell = state.terrain[m.nextTileY * MAP_SIZE + m.nextTileX]!;
    if ((cell & TERRAIN_MINE) !== 0) {
      detonateMine(state, m.nextTileX, m.nextTileY, tank);
    }
  }
  // Tank drove onto a Boat tile → "pick up" the boat. Mirrors WinBolo
  // tank.c:1534-1542: the Boat tile is consumed (becomes River) and the
  // tank becomes a tank-on-boat inheriting its current speed/dir. The
  // existing boat entity machinery then drives subsequent ticks via
  // tickBoats — same code path as a fresh spawn ferry. Without this hook
  // a tank that drove over an LGM-built boat tile was just treated as
  // crossing slow water (max speed 3) instead of becoming the boat.
  if (tank.alive && tank.inBoatId === 0) {
    const cell = state.terrain[m.nextTileY * MAP_SIZE + m.nextTileX]!;
    if ((cell & TERRAIN_MASK) === Terrain.Boat) {
      pickupBoatTile(state, tank, m.nextTileX, m.nextTileY);
    }
  }
  // Drown on DeepSea: a tank standing on DeepSea without a boat is dead.
  // [WinBolo] tank.c:256-274 ("Check for death by drowning") and Cheshire's
  // GameFAQ ("Tank Sunk in Deep Sea"). We check post-movement so a slide
  // off a bridge or off the world edge triggers the kill.
  if (terrainAtRaw(state.terrain, tileX(tank.x), tileY(tank.y)) === Terrain.DeepSea) {
    drownTank(state, tank);
    return;
  }
  // Pickup destroyed pillbox: WinBolo's tankCheckPillCapture (tank.c:1748)
  // runs every tick using the tank's CURRENT tile, not gated on tile-entry —
  // so pickup fires whether you drove onto the rubble or were stopped there
  // when an ally shelled the pill dead. We mirror that: read the tank's
  // post-movement tile (captures both speed-driven and slide-driven
  // translation) and check unconditionally. Silently no-ops when the tile
  // holds no pickup-able pill.
  pickupPillIfDestroyed(state, tank, tileX(tank.x), tileY(tank.y));
  // Water drain: a tank standing in River, not on a boat, slowly loses
  // ammo + mines. Triggered every TANK_WATER_TICKS ticks. (Speed cap on
  // River is already 3 = MAP_SPEED_TRIVER, so any movement on River
  // qualifies.)
  if (terrainAtRaw(state.terrain, tileX(tank.x), tileY(tank.y)) === Terrain.River) {
    tank.waterCount++;
    if (tank.waterCount >= TANK_WATER_TICKS) {
      tank.waterCount = 0;
      if (tank.shells > 0) tank.shells--;
      if (tank.mines > 0) tank.mines--;
    }
  } else {
    tank.waterCount = 0;
  }
}

// Advance the gunsight range one step at most per GUNSIGHT_STEP_TICKS while
// MoreRange / LessRange is held. Releasing the key resets the cooldown so
// the next press registers immediately. Pressing both at once is a no-op.
function tickGunsight(tank: Tank, input: TankInput): void {
  const more = hasKey(input, TankKey.MoreRange);
  const less = hasKey(input, TankKey.LessRange);
  if (!more && !less) {
    tank.sightLenTimer = 0;
    return;
  }
  if (more && less) return; // ambiguous — ignore
  if (tank.sightLenTimer > 0) {
    tank.sightLenTimer--;
    return;
  }
  if (more && tank.sightLen < GUNSIGHT_MAX) tank.sightLen++;
  else if (less && tank.sightLen > GUNSIGHT_MIN) tank.sightLen--;
  tank.sightLenTimer = GUNSIGHT_STEP_TICKS;
}

function pickupBoatTile(state: GameState, tank: Tank, tx: number, ty: number): void {
  // Replace the Boat tile with River (preserve the mine bit just in case —
  // a Boat tile shouldn't carry one, but the mask is cheap).
  const idx = ty * MAP_SIZE + tx;
  state.terrain[idx] = (state.terrain[idx]! & TERRAIN_MINE) | Terrain.River;
  // Mint a fresh boat entity carrying the tank. dir/speed are inherited so
  // the player keeps moving smoothly — no jarring stop. WinBolo doesn't
  // touch speed on entry (tank.c:1538 just flips onBoat=TRUE), same logic.
  const boatId = state.nextEntityId++;
  const boat: Boat = {
    id: boatId,
    riderTankId: tank.id,
    x: tank.x,
    y: tank.y,
    dir: tank.dir,
    speed: Math.min(tank.speed, MAP_SIZE), // boats clamp to their own max in tickBoats
  };
  state.boats.push(boat);
  tank.inBoatId = boatId;
}

export function pickupPillIfDestroyed(
  state: GameState,
  tank: Tank,
  tx: number,
  ty: number,
): void {
  if (tank.carriedPills >= TANK_MAX_CARRIED_PILLS) return;
  const pill = state.pillboxes.find(
    (p) => p.x === tx && p.y === ty && p.armour <= 0 && p.carriedBy === 0,
  );
  if (!pill) return;
  tank.carriedPills++;
  // Mark the pill carried — entity stays in state.pillboxes so its identity
  // (id, ownership, slot in the status grid) survives across pickup. Renderer
  // and pillbox-firing skip pills with carriedBy != 0; on death they're
  // dropped back at the death tile (see damage.ts dropCarriedPills).
  pill.carriedBy = tank.id;
  // Transfer ownership on pickup — matches WinBolo (tank.c:1780-1782) and
  // is the natural moment for "X captured a Neutral Pillbox" / "X just
  // stole pillbox from Y" wire announcements. The pill's pre-pickup owner
  // is what gets stolen (NEUTRAL, an enemy slot, or — in degenerate
  // hand-constructed test states — a former ally; see alliance.ts:99
  // `applyAllianceLeavePayoff` for the now-defensive carry-betrayal
  // path that's a no-op under this pickup-transfer model).
  pill.owner = tank.owner;
  // Cosmetic: a pill destroyed on a Pillbox tile leaves Rubble (set by
  // applyShellHitPill). Picking it up clears the wreckage to Crater.
  // Skip for non-Rubble tiles — a pill that was dropped on grass/road/
  // river/etc. shouldn't blast a Crater into the existing terrain on
  // pickup. Especially load-bearing for water tiles: a boat picking up
  // a pill on River must leave the river intact.
  const idx = ty * MAP_SIZE + tx;
  if ((state.terrain[idx]! & TERRAIN_MASK) === Terrain.Rubble) {
    state.terrain[idx] = (state.terrain[idx]! & TERRAIN_MINE) | Terrain.Crater;
  }
}

function terrainAtRaw(terrain: Uint8Array, x: number, y: number): number {
  if (x < 0 || x >= MAP_SIZE || y < 0 || y >= MAP_SIZE) return Terrain.DeepSea;
  return terrain[y * MAP_SIZE + x]! & TERRAIN_MASK;
}

function clampWorld(v: number): number {
  if (v < 0) return 0;
  if (v > 0xffff) return 0xffff;
  return v;
}
